import { ANSWER_RESTART_EVENT, BaseSseClient, type SseParsedEvent } from '@/stores/local/shared/BaseSseClient';
import { formatToolCallStatus } from '@/utils/toolCallStatus';

import { AssistantCompleteEvent, SseHandlers } from '../types';

import {
  ASSISTANT_START_EVENTS,
  COMPLETE_EVENTS,
  DONE_EVENTS,
  ERROR_EVENTS,
  INFORMATION_SOURCES_EVENTS,
  STATUS_LABELS,
  TOKEN_EVENTS,
  TOOL_CALL_EVENTS,
  TOOL_CALL_RESULT_EVENTS,
  USER_MESSAGE_EVENTS,
} from './config';

type StatusEventName = keyof typeof STATUS_LABELS;

type ToolCallPayload = { id: string; displayName: string };
type ToolCallResultPayload = { toolCallId: string; content: string };

export class ChatSseClient extends BaseSseClient<SseHandlers> {
  private _toolCallSeq = 0;

  private _extractToolCall(event: SseParsedEvent): ToolCallPayload | undefined {
    const { json, data } = event;

    if (json && typeof json === 'object') {
      const id = this.pickString(json, 'id')?.trim() || `tool_call_${(this._toolCallSeq += 1)}`;
      const displayName = this.pickString(json, 'display_name');

      if (displayName && displayName.trim().length > 0) {
        return { id, displayName: displayName.trim() };
      }

      const name = this.pickString(json, 'name');
      const args = (json as Record<string, unknown>).args;
      const formatted = formatToolCallStatus(name, args);

      if (formatted) {
        return { id, displayName: formatted };
      }
    }

    if (typeof data === 'string' && data.length > 0) {
      const id = `tool_call_${(this._toolCallSeq += 1)}`;

      return { id, displayName: data };
    }

    return undefined;
  }

  private _extractToolCallResultPayload(event: SseParsedEvent): ToolCallResultPayload | undefined {
    const { json } = event;

    if (json && typeof json === 'object') {
      const toolCallId = this.pickString(json, 'tool_call_id') ?? '';
      const content = this.pickString(json, 'content')?.trim() ?? '';

      return { toolCallId, content };
    }

    return undefined;
  }

  private _resolveStatusEvent(eventName: string | null, type?: string): StatusEventName | null {
    const candidates = [eventName, type].filter((item): item is string => Boolean(item));

    for (const candidate of candidates) {
      const normalized = candidate.replace(/^analysis_/, '');

      if (normalized in STATUS_LABELS) {
        return normalized as StatusEventName;
      }
    }

    return null;
  }

  private _extractStatusLabel(event: SseParsedEvent): string | undefined {
    // Ищем человеко-читаемый текст статуса в label/content/text/data.
    const { json, data } = event;

    if (json && typeof json === 'object') {
      const candidates = [
        this.pickString(json, 'label'),
        this.pickString(json, 'content'),
        this.pickString(json, 'text'),
        this.pickString(json, 'data'),
      ].filter((item): item is string => item !== undefined);

      if (candidates.length > 0) {
        for (const candidate of candidates) {
          const trimmed = candidate.trim();

          if (trimmed.length > 0) {
            return trimmed;
          }
        }

        return undefined;
      }
    }

    const trimmed = typeof data === 'string' ? data.trim() : '';

    return trimmed.length > 0 ? trimmed : undefined;
  }

  private _extractInformationSources(event: SseParsedEvent): string | undefined {
    const { json, data } = event;

    if (json && typeof json === 'object') {
      const candidates = [
        this.pickString(json, 'data'),
        this.pickString(json, 'content'),
        this.pickString(json, 'text'),
      ].filter((item): item is string => item !== undefined);

      // Если сервер прислал JSON-обертку события, но content/data/text пустые,
      // не фолбэчимся на raw-JSON, чтобы не показывать {"type":"information_sources","data":""} в активности.
      if (candidates.length > 0) {
        for (const candidate of candidates) {
          const trimmed = candidate.trim();

          if (trimmed.length > 0) {
            return trimmed;
          }
        }

        return '';
      }
    }

    return data.length > 0 ? data : undefined;
  }

  private _handleStatusEvent(event: SseParsedEvent, handlers: SseHandlers, eventName: string | null, type?: string) {
    // Сначала обрабатываем статусные ивенты, чтобы они не попадали в общую обработку событий.
    const statusEvent = this._resolveStatusEvent(eventName, type);

    if (!statusEvent) {
      return false;
    }

    const label = this._extractStatusLabel(event) ?? STATUS_LABELS[statusEvent];

    if (label && label.trim().length > 0) {
      handlers.onStatus?.(label);
    }

    return true;
  }

  private _handleToolCallResult(event: SseParsedEvent, handlers: SseHandlers): void {
    const payload = this._extractToolCallResultPayload(event);

    if (payload) {
      handlers.onToolCallResult?.(payload);
    }
  }

  private _handleInformationSources(event: SseParsedEvent, handlers: SseHandlers, content: string): void {
    handlers.onInformationSources?.(this._extractInformationSources(event) ?? content ?? '');
  }

  private _dispatchByEventName(
    eventName: string | null,
    event: SseParsedEvent,
    handlers: SseHandlers,
    content: string,
    json: unknown,
    data: string,
  ): boolean {
    if (!eventName) {
      return false;
    }

    if (USER_MESSAGE_EVENTS.has(eventName)) {
      handlers.onUserMessage?.();

      return true;
    }

    if (ASSISTANT_START_EVENTS.has(eventName)) {
      handlers.onAssistantStart?.();

      return true;
    }

    if (TOKEN_EVENTS.has(eventName)) {
      handlers.onToken?.(content);

      return true;
    }

    if (eventName === ANSWER_RESTART_EVENT) {
      handlers.onAnswerRestart?.(this.extractAnswerRestartMessage(event));

      return true;
    }

    if (TOOL_CALL_EVENTS.has(eventName)) {
      const payload = this._extractToolCall(event);

      if (payload) {
        handlers.onToolCall?.(payload);
      }

      return true;
    }

    if (TOOL_CALL_RESULT_EVENTS.has(eventName)) {
      this._handleToolCallResult(event, handlers);

      return true;
    }

    if (INFORMATION_SOURCES_EVENTS.has(eventName)) {
      this._handleInformationSources(event, handlers, content);

      return true;
    }

    if (COMPLETE_EVENTS.has(eventName)) {
      handlers.onAssistantComplete?.({
        raw: json ?? data,
        text: content,
      } as AssistantCompleteEvent);

      return true;
    }

    if (DONE_EVENTS.has(eventName)) {
      handlers.onDone?.();

      return true;
    }

    if (ERROR_EVENTS.has(eventName)) {
      handlers.onError?.(
        this.pickString(json, 'message') ?? this.pickString(json, 'error') ?? 'Ошибка при формировании ответа',
      );

      return true;
    }

    return false;
  }

  private _dispatchByType(type: string | undefined, event: SseParsedEvent, handlers: SseHandlers, content: string) {
    if (!type) {
      return false;
    }

    if (type === 'tool_call') {
      const payload = this._extractToolCall(event);

      if (payload) {
        handlers.onToolCall?.(payload);
      }

      return true;
    }

    if (type === 'tool_call_result') {
      this._handleToolCallResult(event, handlers);

      return true;
    }

    if (type === 'information_sources') {
      this._handleInformationSources(event, handlers, content);

      return true;
    }

    return false;
  }

  protected handleEvent(event: SseParsedEvent, handlers: SseHandlers): void {
    const { eventName, json, data } = event;
    const type = this.pickString(json, 'type');

    const content = this.pickString(json, 'content') ?? this.pickString(json, 'text') ?? data;

    if (this._handleStatusEvent(event, handlers, eventName, type)) {
      return;
    }

    if (this._dispatchByEventName(eventName, event, handlers, content, json, data)) {
      return;
    }

    this._dispatchByType(type, event, handlers, content);
  }
}
